import { Distribution, closeDistributionModal, setSelectedDistribution } from "actions/collections/collection-settings";
import React from "react";
import { Dispatch, connect } from "react-redux";
import { IStore } from "reducers";
import { useTargetWizardContext } from "../../context/target-wizard-context";
import { TARGET_BAG_ID, TARGET_GROUPS_ID } from "constants/product-collection";
import { AllocationBag } from "helpers/allocation-dimension";
import { formatMessage } from "lib/globalize-util";
import { CrossIcon } from "../delete-target-dialog/cross-icon";
import { GroupedOption, Option, Select } from "./components/select";
import { List, Map } from "immutable";
import "./manage-distribution-modal.css";
import { DATATEST } from "constants/integration";
import { TargetsQueueType, updateQueueTypeAction } from "actions/collections/collection-target";

export const DEFAULT_DISTRIBUTION_OPTION = { value: "default", label: "Even Distribution (default)" };
export const DISTRIBUTION_TYPE_SELECT_ID = "manage-distribution-select";

interface ReduxDispatchProps {
    closeDialog: () => void;
    setSelectedDistribution: (distribution: Distribution) => void;
    updateQueueType: (type: TargetsQueueType) => void;
}

interface ReduxProps {
    selectedDistribution: Distribution;
    targetBag: AllocationBag;
    targetGroups: List<Map<string, any>>;
}

export const ManageDistributionModalogComponent = ({
    closeDialog,
    selectedDistribution,
    targetBag,
    setSelectedDistribution,
    updateQueueType,
    targetGroups,
}: ReduxDispatchProps & ReduxProps) => {
    const dialogRef = React.useRef<HTMLDialogElement>(null);
    const [value, setValue] = React.useState<Distribution>(selectedDistribution);
    const {
        actions: { openNewWizard },
    } = useTargetWizardContext();

    const handleNewTargetButtonClick = () => {
        openNewWizard(targetBag);
        updateQueueType(TargetsQueueType.CREATE);
        closeDialog();
    };

    const onChange = (option: Option) => {
        setValue(option);
    };

    const mappedOptions = React.useMemo(() => {
        return targetGroups?.toJS()?.reduce(
            (acc: GroupedOption, target) => {
                const { name, id } = target?.struct_value || target;
                acc[1].options.push({ label: name, value: id });
                return acc;
            },
            [
                {
                    label: formatMessage("Default"),
                    options: [DEFAULT_DISTRIBUTION_OPTION],
                },
                {
                    label: formatMessage("Targets"),
                    options: [],
                },
            ]
        );
    }, [targetGroups]);

    const handleApply = () => {
        setSelectedDistribution(value);
        closeDialog();
    };

    React.useEffect(() => {
        dialogRef.current?.showModal();
        document.body.classList.add("modal-open"); // prevent bg scroll
    }, []);

    return (
        <dialog ref={dialogRef} className="manage-distribution-modal">
            {/* Header */}
            <div className="manage-distribution-modal-title-wrapper">
                <h1 className="manage-distribution-modal-title">
                    <div>{formatMessage("Manage distribution")}</div>
                </h1>
                <button
                    data-test={DATATEST.MANAGE_DISTRIBUTION.CLOSE_DIALOG_BTN}
                    onClick={closeDialog}
                    className="manage-distribution-modal-cross-button"
                >
                    <CrossIcon />
                </button>
            </div>
            <div className="manage-distribution-modal-divider" />
            {/* Body */}
            <div className="manage-distribution-modal-body">
                <div>{formatMessage("Choose distribution type")}</div>
                <div>
                    <label className="manage-distribution-modal-input-label">
                        {formatMessage("Distribution type")}
                    </label>
                    <div>
                        <Select
                            value={value}
                            defaultValue={DEFAULT_DISTRIBUTION_OPTION}
                            placeholder={formatMessage("Select a distribution type")}
                            onChange={onChange}
                            options={mappedOptions}
                            id={DISTRIBUTION_TYPE_SELECT_ID}
                        />
                    </div>
                </div>
            </div>
            <div className="manage-distribution-modal-divider" />
            {/* Footer */}
            <div className="manage-distribution-modal-footer">
                <button className="manage-distribution-modal-link-button" onClick={handleNewTargetButtonClick}>
                    {formatMessage("Create new target")}
                </button>
                <button className="manage-distribution-modal-button primary" onClick={handleApply}>
                    {formatMessage("Apply")}
                </button>
            </div>
        </dialog>
    );
};

const mapStateToProps = (state: IStore) => ({
    selectedDistribution: state.collection.selectedDistribution,
    targetBag: state.mutate.snapshots.get(TARGET_BAG_ID),
    targetGroups: state.mutate.snapshots.get(TARGET_GROUPS_ID),
});

const mapDispatchToProps = (dispatch: Dispatch<IStore>) => ({
    closeDialog: () => dispatch(closeDistributionModal()),
    setSelectedDistribution: (distribution: Distribution) => dispatch(setSelectedDistribution(distribution)),
    updateQueueType: (type: TargetsQueueType) => dispatch(updateQueueTypeAction(type)),
});

export const ManageDistributionModal = connect(mapStateToProps, mapDispatchToProps)(ManageDistributionModalogComponent);
